(() => {
  if (window.BrainGamesPageEditor) return;
  window.BrainGamesPageEditor = true;
  const tabSelector = '[data-section="event_page_tabs"]';
  const initialized = new WeakSet();
  const panels = section => [...section.querySelectorAll('.section-tabs > ul > li[data-tab]')];
  const buttons = section => [...section.querySelectorAll('.section-buttons button[data-tab]')];
  function activate(section, name, focus = false) {
    buttons(section).forEach(button => {
      const active = button.dataset.tab === name;
      button.classList.toggle('style--colourAlt', !active);
      button.classList.toggle('style--noBackground', !active);
      button.classList.toggle('active', active);
      button.setAttribute('aria-selected', String(active));
      button.tabIndex = active ? 0 : -1;
      if (active && focus) button.focus();
    });
    panels(section).forEach(panel => {
      const active = panel.dataset.tab === name;
      panel.hidden = !active;
      panel.style.display = active ? '' : 'none';
      if (active) panel.removeAttribute('data-hide');
      else panel.dataset.hide = 'true';
      if (active) panel.querySelectorAll('.swiper').forEach(el => el.swiper?.update());
    });
  }
  function initialize(root = document) {
    const banners = [...root.querySelectorAll('[data-countdown-sticky]')];
    if (root.matches?.('[data-countdown-sticky]')) banners.unshift(root);
    banners.forEach(banner => banner.parentElement?.classList.toggle('style--sticky', banner.dataset.countdownSticky === 'true'));
    const sections = [...root.querySelectorAll(tabSelector)];
    if (root.matches?.(tabSelector)) sections.unshift(root);
    sections.forEach(section => {
      if (initialized.has(section)) return;
      initialized.add(section);
      section.querySelector('.section-buttons')?.setAttribute('role', 'tablist');
      const items = buttons(section);
      const contents = panels(section);
      items.forEach((button, index) => {
        const panel = contents.find(item => item.dataset.tab === button.dataset.tab);
        button.id = `${section.id}-tab-${index}`;
        button.setAttribute('role', 'tab');
        if (panel) {
          panel.id = `${section.id}-panel-${index}`;
          panel.setAttribute('role', 'tabpanel');
          panel.setAttribute('aria-labelledby', button.id);
          button.setAttribute('aria-controls', panel.id);
        }
      });
      if (items.length) activate(section, items[0].dataset.tab);
    });
  }
  document.addEventListener('click', event => {
    const button = event.target.closest?.(`${tabSelector} .section-buttons button[data-tab]`);
    if (button) activate(button.closest(tabSelector), button.dataset.tab);
  });
  document.addEventListener('keydown', event => {
    const button = event.target.closest?.(`${tabSelector} .section-buttons button[data-tab]`);
    if (!button || !['ArrowLeft', 'ArrowRight', 'Home', 'End'].includes(event.key)) return;
    event.preventDefault();
    const section = button.closest(tabSelector);
    const items = buttons(section);
    const index = items.indexOf(button);
    const next = event.key === 'Home' ? 0 : event.key === 'End' ? items.length - 1 : (index + (event.key === 'ArrowRight' ? 1 : -1) + items.length) % items.length;
    activate(section, items[next].dataset.tab, true);
  });
  document.addEventListener('shopify:section:load', event => initialize(event.target));
  document.addEventListener('shopify:block:select', event => {
    const tabSection = event.target.closest?.(tabSelector);
    const panel = event.target.closest?.('.section-tabs > ul > li[data-tab]');
    if (tabSection && panel) activate(tabSection, panel.dataset.tab);
    const slide = event.target.closest?.('.swiper-slide');
    const swiper = slide?.closest('.swiper')?.swiper;
    if (swiper && slide) {
      swiper.autoplay?.stop();
      const slides = [...swiper.slides];
      const index = Number(slide.dataset.swiperSlideIndex ?? slides.indexOf(slide));
      if (swiper.params.loop) swiper.slideToLoop(index, 0);
      else swiper.slideTo(index, 0);
    }
  });
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => initialize(), { once: true });
  else initialize();

  class CountdownElement extends HTMLElement {
    connectedCallback() {
      this.disconnectTimer();
      this.deadline = this.parseDeadline(this.dataset.date, this.dataset.time || '00:00', this.dataset.timeZone || 'Europe/Riga');
      this.tick();
      if (this.dataset.state === 'running') this.timer = setInterval(() => this.tick(), 1000);
    }
    disconnectedCallback() { this.disconnectTimer(); }
    disconnectTimer() { clearInterval(this.timer); this.timer = null; }
    parseDeadline(date, time, zone) {
      if (!/^\d{4}-\d{2}-\d{2}$/.test(date || '') || !/^([01]\d|2[0-3]):[0-5]\d$/.test(time)) return NaN;
      const [year, month, day] = date.split('-').map(Number);
      const [hour, minute] = time.split(':').map(Number);
      const wanted = Date.UTC(year, month - 1, day, hour, minute);
      const normalized = new Date(wanted);
      if (normalized.getUTCFullYear() !== year || normalized.getUTCMonth() !== month - 1 || normalized.getUTCDate() !== day) return NaN;
      try {
        const formatter = new Intl.DateTimeFormat('en-GB', { timeZone: zone, year: 'numeric', month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit', second: '2-digit', hourCycle: 'h23' });
        const wallTime = timestamp => {
          const values = Object.fromEntries(formatter.formatToParts(timestamp).map(part => [part.type, part.value]));
          return Date.UTC(+values.year, +values.month - 1, +values.day, +values.hour, +values.minute, +values.second);
        };
        let candidate = wanted;
        for (let index = 0; index < 3; index += 1) candidate += wanted - wallTime(candidate);
        return wallTime(candidate) === wanted ? candidate : NaN;
      } catch { return NaN; }
    }
    tick() {
      const seconds = Math.max(0, Math.floor((this.deadline - Date.now()) / 1000));
      const valid = Number.isFinite(seconds);
      this.dataset.state = !valid ? 'invalid' : seconds > 0 ? 'running' : 'expired';
      const values = { days: valid ? Math.floor(seconds / 86400) : 0, hours: valid ? Math.floor(seconds % 86400 / 3600) : 0, minutes: valid ? Math.floor(seconds % 3600 / 60) : 0, seconds: valid ? seconds % 60 : 0 };
      for (const [unit, value] of Object.entries(values)) {
        const field = this.querySelector(`[data-${unit}]`);
        if (!field) continue;
        field.textContent = `${unit === 'days' ? value : String(value).padStart(2, '0')} ${this.dataset[`${unit}Label`] || ''}`.trim();
        field.hidden = unit === 'days' && value === 0;
      }
      if (this.dataset.state !== 'running') {
        this.disconnectTimer();
        const section = this.closest('section');
        if (section && !window.Shopify?.designMode && this.dataset.hideExpired !== 'false') section.hidden = true;
      }
    }
  }
  if (!customElements.get('countdown-element')) customElements.define('countdown-element', CountdownElement);
  class EventCalendar extends HTMLElement {
    connectedCallback() {
      this.abort?.abort();
      this.abort = new AbortController();
      const signal = this.abort.signal;
      for (const type of ['type', 'place']) {
        const select = this.querySelector(`[data-filter="${type}"]`);
        if (!select) continue;
        while (select.options.length > 1) select.remove(1);
        const values = [...new Set([...this.querySelectorAll('[data-event]')].map(item => item.dataset[type]).filter(Boolean))].sort();
        values.forEach(value => select.add(new Option(value, value)));
      }
      this.addEventListener('change', () => this.filter(), { signal });
      this.addEventListener('submit', event => event.preventDefault(), { signal });
      this.addEventListener('reset', () => setTimeout(() => this.isConnected && this.filter(), 0), { signal });
      this.addEventListener('shopify:block:select', event => {
        const item = event.target.closest('[data-event]');
        if (!item) return;
        item.hidden = false;
        item.querySelector('details').open = true;
      }, { signal });
      this.addEventListener('click', event => {
        const button = event.target.closest('[data-calendar-download]');
        if (button) this.download(button);
      }, { signal });
      this.filter();
    }
    disconnectedCallback() { this.abort?.abort(); }
    filter() {
      const value = name => this.querySelector(`[data-filter="${name}"]`)?.value || '';
      const from = value('from');
      const to = value('to');
      const type = value('type');
      const place = value('place');
      const past = this.querySelector('[data-filter="past"]')?.checked || window.Shopify?.designMode;
      let count = 0;
      this.querySelectorAll('[data-event]').forEach(item => {
        const start = item.dataset.start;
        const end = item.dataset.end || start;
        const visible = (past || !end || end >= this.dataset.today) && (!from || !end || end >= from) && (!to || !start || start <= to) && (!type || item.dataset.type === type) && (!place || item.dataset.place === place);
        item.hidden = !visible;
        if (visible) count += 1;
      });
      const empty = this.querySelector('[data-empty]');
      if (empty) empty.hidden = count > 0;
    }
    createCalendar(data) {
      const escape = text => String(text || '').replace(/\\/g, '\\\\').replace(/\r?\n/g, '\\n').replace(/;/g, '\\;').replace(/,/g, '\\,');
      const stamp = timestamp => new Date(timestamp).toISOString().replace(/[-:]/g, '').replace(/\.\d{3}Z$/, 'Z');
      const parse = (date, time) => CountdownElement.prototype.parseDeadline(date, time, this.dataset.timeZone || 'Europe/Riga');
      const start = parse(data.date, data.time || '00:00');
      if (!Number.isFinite(start)) return null;
      const lines = ['BEGIN:VCALENDAR', 'VERSION:2.0', 'PRODID:-//Brain Games//Events//EN', 'BEGIN:VEVENT', `UID:${escape(data.id || `${data.date}-${data.title}`)}@brain-games`, `DTSTAMP:${stamp(Date.now())}`, `SUMMARY:${escape(data.title)}`];
      lines.push(data.time ? `DTSTART:${stamp(start)}` : `DTSTART;VALUE=DATE:${data.date.replaceAll('-', '')}`);
      const end = data.endTime ? parse(data.endDate || data.date, data.endTime) : NaN;
      if (Number.isFinite(end) && end > start) lines.push(`DTEND:${stamp(end)}`);
      if (data.location || data.address) lines.push(`LOCATION:${escape([data.location, data.address].filter(Boolean).join(', '))}`);
      lines.push('END:VEVENT', 'END:VCALENDAR');
      const encoder = new TextEncoder();
      return lines.map(line => {
        let length = 0;
        let result = '';
        for (const char of line) {
          const bytes = encoder.encode(char).length;
          if (length + bytes > 74) { result += '\r\n '; length = 1; }
          result += char;
          length += bytes;
        }
        return result;
      }).join('\r\n') + '\r\n';
    }
    download(button) {
      const content = this.createCalendar({ ...button.dataset, id: button.closest('[data-event]')?.dataset.eventId });
      if (!content) return;
      const url = URL.createObjectURL(new Blob([content], { type: 'text/calendar;charset=utf-8' }));
      const anchor = document.createElement('a');
      anchor.href = url;
      anchor.download = `${button.dataset.title?.replace(/[^\p{L}\p{N}-]+/gu, '-') || 'event'}.ics`;
      document.body.append(anchor);
      anchor.click();
      anchor.remove();
      setTimeout(() => URL.revokeObjectURL(url), 1000);
    }
  }
  if (!customElements.get('bg-event-calendar')) customElements.define('bg-event-calendar', EventCalendar);
})();
